/**
 * MenuSchedule — programmazione automatica dei menu per giorno e fascia oraria.
 * Admin-only. Le schedule attivano automaticamente i menu corrispondenti ogni minuto.
 */
import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Clock, Calendar, Power, PowerOff } from "lucide-react";

const DAYS = [
    { id: 0, label: "Lun" }, { id: 1, label: "Mar" }, { id: 2, label: "Mer" },
    { id: 3, label: "Gio" }, { id: 4, label: "Ven" }, { id: 5, label: "Sab" }, { id: 6, label: "Dom" },
];

const MenuSchedulePage = () => {
    const [schedules, setSchedules] = useState([]);
    const [menus, setMenus] = useState([]);
    const [showNew, setShowNew] = useState(false);

    const [name, setName] = useState("");
    const [selectedMenus, setSelectedMenus] = useState([]);
    const [selectedDays, setSelectedDays] = useState([0, 1, 2, 3, 4]);
    const [from, setFrom] = useState("12:00");
    const [to, setTo] = useState("15:00");
    const [priority, setPriority] = useState(0);

    const load = async () => {
        try {
            const [s, m] = await Promise.all([api.get("/menu-schedules"), api.get("/menus")]);
            setSchedules(s.data);
            setMenus(m.data);
        } catch (e) { toast.error(formatError(e)); }
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!name.trim() || selectedMenus.length === 0 || selectedDays.length === 0) {
            toast.error("Compila nome, menu e giorni"); return;
        }
        try {
            await api.post("/menu-schedules", {
                name, menu_ids: selectedMenus, days: selectedDays,
                time_from: from, time_to: to, enabled: true, priority,
            });
            toast.success("Programmazione creata");
            setShowNew(false);
            setName(""); setSelectedMenus([]); setSelectedDays([0, 1, 2, 3, 4]);
            setFrom("12:00"); setTo("15:00"); setPriority(0);
            load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const toggle = async (s) => {
        try {
            await api.patch(`/menu-schedules/${s.id}`, { enabled: !s.enabled });
            load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const del = async (s) => {
        if (!window.confirm(`Eliminare "${s.name}"?`)) return;
        try { await api.delete(`/menu-schedules/${s.id}`); load(); }
        catch (e) { toast.error(formatError(e)); }
    };

    return (
        <div className="space-y-5" data-testid="menu-schedule-page">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-brand">Pianificazione</div>
                    <h1 className="font-display text-3xl font-bold">Programmazione Menu</h1>
                    <p className="text-xs text-elegant mt-1">Imposta giorni e orari: il sistema attiva automaticamente i menu giusti.</p>
                </div>
                <button onClick={() => setShowNew(true)} data-testid="schedule-new-btn"
                    className="h-12 px-5 bg-brand text-deep font-bold rounded-md flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Nuova programmazione
                </button>
            </div>

            <div className="space-y-2" data-testid="schedule-list">
                {schedules.length === 0 ? (
                    <div className="bg-surface border border-[#1f2c44] rounded-lg p-8 text-center text-elegant">
                        Nessuna programmazione attiva. Crea la prima per automatizzare i cambi menu.
                    </div>
                ) : schedules.map((s) => (
                    <div key={s.id} className={`bg-surface border rounded-lg p-4 flex items-center justify-between gap-3 ${s.enabled ? "border-brand/40" : "border-[#1f2c44] opacity-60"}`}
                        data-testid={`schedule-${s.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-warm">{s.name}</div>
                            <div className="text-xs text-elegant flex flex-wrap gap-2 mt-1">
                                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {s.days.map(d => DAYS[d]?.label).join(", ")}</span>
                                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {s.time_from} - {s.time_to}</span>
                                <span>{(s.menu_ids || []).length} menu</span>
                                {s.priority > 0 && <span className="text-amber-300">priorità {s.priority}</span>}
                            </div>
                            <div className="text-[10px] text-elegant mt-1">
                                {(s.menu_ids || []).map(mid => menus.find(m => m.id === mid)?.name).filter(Boolean).join(" + ")}
                            </div>
                        </div>
                        <button onClick={() => toggle(s)} title={s.enabled ? "Disabilita" : "Abilita"}
                            className={`w-10 h-10 rounded ${s.enabled ? "text-emerald-300 hover:bg-emerald-900/20" : "text-elegant hover:bg-elegant/10"}`}>
                            {s.enabled ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                        </button>
                        <button onClick={() => del(s)} className="w-10 h-10 text-red-400 hover:bg-red-900/20 rounded">
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                ))}
            </div>

            {showNew && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
                    <div onClick={e => e.stopPropagation()} className="bg-surface border-2 border-brand/60 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="schedule-modal">
                        <div className="font-display text-xl font-bold mb-4">Nuova programmazione</div>
                        <div className="space-y-3">
                            <div>
                                <label className="text-[10px] uppercase text-elegant block mb-1">Nome</label>
                                <input value={name} onChange={e => setName(e.target.value)} placeholder="es. Pranzo lun-ven"
                                    data-testid="schedule-name-input"
                                    className="w-full h-10 px-3 bg-deep border border-[#1f2c44] rounded text-warm focus:border-brand focus:outline-none" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase text-elegant block mb-1">Menu da attivare (1+)</label>
                                <div className="space-y-1 max-h-40 overflow-y-auto">
                                    {menus.map(m => (
                                        <label key={m.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer ${selectedMenus.includes(m.id) ? "bg-brand/20 border border-brand/60" : "bg-deep border border-[#1f2c44]"}`}>
                                            <input type="checkbox" checked={selectedMenus.includes(m.id)}
                                                onChange={() => setSelectedMenus(p => p.includes(m.id) ? p.filter(x => x !== m.id) : [...p, m.id])}
                                                className="accent-brand" />
                                            <span className="text-sm text-warm">{m.name}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] uppercase text-elegant block mb-1">Giorni</label>
                                <div className="flex gap-1">
                                    {DAYS.map(d => (
                                        <button key={d.id} onClick={() => setSelectedDays(p => p.includes(d.id) ? p.filter(x => x !== d.id) : [...p, d.id])}
                                            data-testid={`schedule-day-${d.id}`}
                                            className={`flex-1 h-10 rounded font-bold text-xs ${selectedDays.includes(d.id) ? "bg-brand text-deep" : "bg-deep border border-[#1f2c44] text-elegant"}`}>
                                            {d.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <div>
                                    <label className="text-[10px] uppercase text-elegant block mb-1">Dalle</label>
                                    <input type="time" value={from} onChange={e => setFrom(e.target.value)}
                                        className="w-full h-10 px-2 bg-deep border border-[#1f2c44] rounded text-warm" />
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase text-elegant block mb-1">Alle</label>
                                    <input type="time" value={to} onChange={e => setTo(e.target.value)}
                                        className="w-full h-10 px-2 bg-deep border border-[#1f2c44] rounded text-warm" />
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase text-elegant block mb-1">Priorità</label>
                                    <input type="number" min="0" max="100" value={priority} onChange={e => setPriority(parseInt(e.target.value) || 0)}
                                        className="w-full h-10 px-2 bg-deep border border-[#1f2c44] rounded text-warm text-right" />
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2 mt-5">
                            <button onClick={() => setShowNew(false)} className="flex-1 h-11 border border-[#1f2c44] rounded text-elegant uppercase tracking-wider text-xs font-bold">Annulla</button>
                            <button onClick={create} data-testid="schedule-create-btn" className="flex-[1.5] h-11 bg-brand text-deep font-bold rounded uppercase tracking-wider text-xs">Crea</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MenuSchedulePage;
